import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { useTier } from '../../hooks/useTier';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { SearchableMultiSelect } from '../../components/ui/searchable-multi-select';
import { CredentialDialog, type CredentialInfo } from '../../components/CredentialDialog';

interface MasterItem {
  id: string;
  username: string;
  displayName: string;
  status: 'active' | 'inactive';
  merchantCount: number;
}
interface SubMdrItem {
  id: string;
  username: string;
  displayName: string;
  status: 'active' | 'inactive';
}
interface AssignedMerchant {
  id: string;
  merchantId: string;
  merchantCode: string;
  merchantName: string;
  masterPayinFeePercent: number;
  masterPayoutFeePercent: number;
}
interface AssignableMerchant {
  merchantId: string;
  code: string;
  name: string;
  sellerId: string;
  sellerCode: string;
  sellerName: string;
  assignedMasterMdrId: string | null;
}

function MasterMdrDialog({
  companyId,
  onClose,
  onCredential,
}: {
  companyId: string;
  onClose: () => void;
  onCredential: (c: CredentialInfo) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ username: '', displayName: '', password: '' });
  const [serverError, setServerError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiPost<MasterItem & { tempPassword?: string }>(adminCompanyPath('/mdr', companyId), {
        username: form.username,
        displayName: form.displayName,
        password: form.password || undefined,
      }),
    onSuccess: (res) => {
      if (!res.ok) {
        setServerError(res.error.message);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-masters'] });
      if (res.data.tempPassword) {
        onCredential({
          name: res.data.displayName,
          username: res.data.username,
          tempPassword: res.data.tempPassword,
        });
      }
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('mdr.createMaster')}
        </h3>
        {serverError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            {serverError}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setServerError(null);
            saveMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.username')}
            </label>
            <Input
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('mdr.displayName')}
            </label>
            <Input
              value={form.displayName}
              onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('users.passwordOptional')}
            </label>
            <Input
              type="text"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              placeholder={t('users.passwordPlaceholder')}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saveMutation.isPending} className="flex-1">
              {saveMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AdminMdr() {
  const { t } = useTranslation();
  const { activeCompanyId } = useCompany();
  const { isSuper, canManage } = useTier();
  const queryClient = useQueryClient();

  const [selectedMaster, setSelectedMaster] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [credential, setCredential] = useState<CredentialInfo | null>(null);

  const [assignSellerId, setAssignSellerId] = useState('');
  const [assignMerchantIds, setAssignMerchantIds] = useState<string[]>([]);
  const [assignPayin, setAssignPayin] = useState('0');
  const [assignPayout, setAssignPayout] = useState('0');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignMsg, setAssignMsg] = useState<string | null>(null);

  const base = (p: string) => adminCompanyPath(`/mdr${p}`, activeCompanyId!);

  const { data: mastersData, isLoading } = useQuery({
    queryKey: ['admin', 'mdr-masters', activeCompanyId],
    queryFn: () => apiGet<{ items: MasterItem[]; total: number }>(base('')),
    enabled: !!activeCompanyId,
  });

  const { data: subMdrsData } = useQuery({
    queryKey: ['admin', 'mdr-sub-mdrs', activeCompanyId, selectedMaster],
    queryFn: () => apiGet<{ items: SubMdrItem[] }>(base(`/${selectedMaster}/sub-mdrs`)),
    enabled: !!activeCompanyId && !!selectedMaster,
  });

  // Reset either a master account or one of its subs — both return the same
  // { username, displayName, tempPassword } shape.
  const resetPassword = useMutation({
    mutationFn: (path: string) =>
      apiPost<{ username: string; displayName: string; tempPassword: string }>(base(path)),
    onSuccess: (res) => {
      if (!res.ok) return;
      setCredential({
        name: res.data.displayName,
        username: res.data.username,
        tempPassword: res.data.tempPassword,
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-sub-mdrs'] });
    },
  });

  const { data: assignedData } = useQuery({
    queryKey: ['admin', 'mdr-master-merchants', activeCompanyId, selectedMaster],
    queryFn: () => apiGet<{ items: AssignedMerchant[] }>(base(`/${selectedMaster}/merchants`)),
    enabled: !!activeCompanyId && !!selectedMaster,
  });

  const { data: assignableData } = useQuery({
    queryKey: ['admin', 'mdr-assignable', activeCompanyId],
    queryFn: () => apiGet<{ items: AssignableMerchant[] }>(base('/assignable-merchants')),
    enabled: !!activeCompanyId && !!selectedMaster,
  });

  const assignMerchant = useMutation({
    // Assign every selected merchant with the same master fees. Reuses the
    // single-merchant endpoint per id so partial failures are reported.
    mutationFn: async () => {
      const results = await Promise.all(
        assignMerchantIds.map((merchantId) =>
          apiPost(base(`/${selectedMaster}/merchants`), {
            merchantId,
            masterPayinFeePercent: Number(assignPayin),
            masterPayoutFeePercent: Number(assignPayout),
          }),
        ),
      );
      const failed = results.filter((r) => !r.ok).length;
      return { total: assignMerchantIds.length, failed };
    },
    onSuccess: ({ total, failed }) => {
      setAssignMerchantIds([]);
      setAssignError(failed > 0 ? t('mdr.assignSomeFailed', { failed }) : null);
      setAssignMsg(t('mdr.assignedCount', { count: total - failed }));
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-master-merchants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-assignable'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-masters'] });
    },
  });

  const unassignMerchant = useMutation({
    mutationFn: (merchantId: string) =>
      apiDelete(base(`/${selectedMaster}/merchants/${merchantId}`)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-master-merchants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-assignable'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'mdr-masters'] });
    },
  });

  const masters = mastersData?.ok ? mastersData.data.items : [];
  const subMdrs = subMdrsData?.ok ? subMdrsData.data.items : [];
  const assigned = assignedData?.ok ? assignedData.data.items : [];
  const assignable = assignableData?.ok ? assignableData.data.items : [];
  // Only merchants not yet assigned to any master can be assigned.
  const unassigned = assignable.filter((m) => !m.assignedMasterMdrId);
  // Step 1: sellers that still have at least one unassigned merchant.
  const sellerOptions = [...new Map(unassigned.map((m) => [m.sellerId, m])).values()]
    .map((m) => ({ value: m.sellerId, label: `${m.sellerCode} — ${m.sellerName}` }))
    .sort((a, b) => a.label.localeCompare(b.label));
  // Step 2: merchants of the chosen seller (already filtered to unassigned).
  const merchantOptions = unassigned
    .filter((m) => m.sellerId === assignSellerId)
    .map((m) => ({ value: m.merchantId, label: `${m.code} — ${m.name}` }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('mdr.adminTitle')}
        </h1>
        {isSuper && (
          <Button size="sm" onClick={() => setShowCreate(true)} disabled={!activeCompanyId}>
            {t('mdr.createMaster')}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('mdr.mastersTitle')} ({masters.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-4 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && masters.length === 0 && (
            <div className="p-4 text-gray-400">{t('common.noData')}</div>
          )}
          {masters.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('mdr.displayName')}</TableHead>
                    <TableHead>{t('auth.username')}</TableHead>
                    <TableHead className="text-right">{t('mdr.merchantCount')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {masters.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>{m.displayName}</TableCell>
                      <TableCell className="font-mono text-xs">{m.username}</TableCell>
                      <TableCell className="text-right">{m.merchantCount}</TableCell>
                      <TableCell className="space-x-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedMaster(m.id)}>
                          {t('mdr.manageMerchants')}
                        </Button>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={resetPassword.isPending}
                            onClick={() => {
                              if (confirm(t('sellers.confirmReset', { name: m.displayName })))
                                resetPassword.mutate(`/${m.id}/reset-password`);
                            }}
                          >
                            {t('sellers.resetPassword')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedMaster && (
        <Card>
          <CardHeader>
            <CardTitle>{t('mdr.assignedMerchants')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {canManage && (
              <div className="grid grid-cols-1 items-end gap-3 rounded-md border border-gray-200 p-3 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-5">
                <div className="text-sm">
                  <span className="mb-1 block text-gray-500">{t('sellers.title')}</span>
                  <SearchableSelect
                    value={assignSellerId}
                    onChange={(v) => {
                      setAssignSellerId(v);
                      setAssignMerchantIds([]);
                    }}
                    placeholder={t('mdr.pickSeller')}
                    searchPlaceholder={t('common.search')}
                    emptyText={t('common.noData')}
                    options={sellerOptions}
                  />
                </div>
                <div className="text-sm">
                  <span className="mb-1 block text-gray-500">{t('merchants.title')}</span>
                  <SearchableMultiSelect
                    values={assignMerchantIds}
                    onChange={setAssignMerchantIds}
                    placeholder={t('mdr.pickMerchants')}
                    searchPlaceholder={t('common.search')}
                    emptyText={assignSellerId ? t('common.noData') : t('mdr.pickSellerFirst')}
                    selectedLabel={(n) => t('mdr.merchantsSelected', { count: n })}
                    disabled={!assignSellerId}
                    options={merchantOptions}
                  />
                </div>
                <label className="text-sm">
                  <span className="mb-1 block text-gray-500">{t('mdr.masterPayinFee')} (%)</span>
                  <Input
                    type="number"
                    step="0.000001"
                    value={assignPayin}
                    onChange={(e) => setAssignPayin(e.target.value)}
                  />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-gray-500">{t('mdr.masterPayoutFee')} (%)</span>
                  <Input
                    type="number"
                    step="0.000001"
                    value={assignPayout}
                    onChange={(e) => setAssignPayout(e.target.value)}
                  />
                </label>
                <Button
                  size="sm"
                  disabled={assignMerchantIds.length === 0 || assignMerchant.isPending}
                  onClick={() => assignMerchant.mutate()}
                >
                  {assignMerchant.isPending ? t('common.loading') : t('mdr.assignMerchant')}
                </Button>
              </div>
            )}
            {assignMsg && <p className="text-sm text-green-600">{assignMsg}</p>}
            {assignError && <p className="text-sm text-red-600">{assignError}</p>}

            {assigned.length === 0 ? (
              <div className="text-gray-400">{t('common.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('merchants.code')}</TableHead>
                      <TableHead>{t('merchants.name')}</TableHead>
                      <TableHead className="text-right">{t('mdr.masterPayinFee')}</TableHead>
                      <TableHead className="text-right">{t('mdr.masterPayoutFee')}</TableHead>
                      {canManage && (
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assigned.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-mono text-xs">{a.merchantCode}</TableCell>
                        <TableCell>{a.merchantName}</TableCell>
                        <TableCell className="text-right">{a.masterPayinFeePercent}%</TableCell>
                        <TableCell className="text-right">{a.masterPayoutFeePercent}%</TableCell>
                        {canManage && (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => unassignMerchant.mutate(a.merchantId)}
                            >
                              {t('mdr.unassign')}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {selectedMaster && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('mdr.subMdrsTitle')} ({subMdrs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {subMdrs.length === 0 ? (
              <div className="p-4 text-gray-400">{t('common.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('mdr.displayName')}</TableHead>
                      <TableHead>{t('auth.username')}</TableHead>
                      <TableHead>{t('common.status')}</TableHead>
                      {canManage && (
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subMdrs.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>{s.displayName}</TableCell>
                        <TableCell className="font-mono text-xs">{s.username}</TableCell>
                        <TableCell>
                          <Badge variant={s.status === 'active' ? 'success' : 'danger'}>
                            {t(`common.${s.status}`)}
                          </Badge>
                        </TableCell>
                        {canManage && (
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={resetPassword.isPending}
                              onClick={() => {
                                if (confirm(t('sellers.confirmReset', { name: s.displayName })))
                                  resetPassword.mutate(
                                    `/${selectedMaster}/sub-mdrs/${s.id}/reset-password`,
                                  );
                              }}
                            >
                              {t('sellers.resetPassword')}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showCreate && isSuper && activeCompanyId && (
        <MasterMdrDialog
          companyId={activeCompanyId}
          onClose={() => setShowCreate(false)}
          onCredential={setCredential}
        />
      )}

      {credential && (
        <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
      )}
    </div>
  );
}
